import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mail, MapPin, Phone, MessageCircle, Building2, Landmark, Linkedin, Instagram, Github } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Reveal, Magnetic } from "./motion";
import { SERVICE_OPTIONS, BUDGET_OPTIONS, CORPORATE_ADDRESS, CORPORATE_ADDRESS_2, REGISTERED_ADDRESS, PRIMARY_EMAIL, PRIMARY_PHONE, SOCIAL_LINKS } from "../data";
import api from "../lib/api";
import { captureCrmLead } from "../lib/crm";

const initial = { name: "", company: "", email: "", phone: "", service: "", budget: "", message: "" };

export const Contact = ({ defaultService = "" }) => {
  const [form, setForm] = useState({ ...initial, service: defaultService });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    corporate_address: CORPORATE_ADDRESS,
    corporate_address_2: CORPORATE_ADDRESS_2,
    registered_address: REGISTERED_ADDRESS,
    email: PRIMARY_EMAIL,
    phone: PRIMARY_PHONE,
    whatsapp: PRIMARY_PHONE
  });

  useEffect(() => {
    api.get("/settings")
      .then((res) => {
        if (res.data) setSettings(res.data);
      })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in your name, email and message."); return; }
    setLoading(true);
    try {
      // Direct CRM webhook capture
      captureCrmLead({
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service || "General Inquiry",
        notes: form.message,
        company: form.company,
        budget: form.budget,
      });

      await api.post("/contact", form);
      toast.success("Thanks! We'll be in touch within 24 hours.");
      setForm({ ...initial, service: defaultService });
    }
    catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  const inputCls = "w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3.5 text-sm outline-none focus:border-[#FF5A1F] transition-colors placeholder:text-neutral-400 text-[#0B0B0B]";

  const cleanPhone = (settings.whatsapp || PRIMARY_PHONE).replace(/[^0-9]/g, "");

  return (
    <section id="contact" className="py-28 px-5 sm:px-8 scroll-mt-20">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-14">
          <div>
            <Reveal><span className="inline-block text-[#FF5A1F] font-heading text-sm font-semibold uppercase tracking-[0.2em] mb-4">Let's talk</span></Reveal>
            <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-[#0B0B0B]">Start your next<br />breakthrough</h2></Reveal>
            <Reveal delay={0.2}><p className="mt-5 text-lg text-neutral-500 leading-relaxed">Tell us about your project. We'll get back within one business day with a plan to move forward.</p></Reveal>
            
            <div className="mt-9 space-y-4">
              <Reveal delay={0.3}>
                <div className="flex flex-col gap-3">
                  <a href={`mailto:${settings.email || PRIMARY_EMAIL}`} className="flex items-center gap-2 hover:text-[#FF5A1F] transition-colors">
                    <span className="text-xs font-bold uppercase tracking-wider text-neutral-400">Email:</span>
                    <span className="text-neutral-800 font-semibold text-sm">{settings.email || PRIMARY_EMAIL}</span>
                  </a>

                  <a href={`tel:${settings.phone || PRIMARY_PHONE}`} className="flex items-center gap-2 hover:text-[#FF5A1F] transition-colors">
                    <span className="text-xs font-bold uppercase tracking-wider text-neutral-400">Phone:</span>
                    <span className="text-neutral-800 font-semibold text-sm">{settings.phone || PRIMARY_PHONE}</span>
                  </a>

                  <a href={`https://wa.me/${cleanPhone}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-emerald-600 transition-colors">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">WhatsApp:</span>
                    <span className="text-neutral-800 font-semibold text-sm">{settings.phone || PRIMARY_PHONE}</span>
                  </a>

                  {/* Official Social Links */}
                  <div className="flex items-center gap-2 pt-1.5">
                    <a
                      href={SOCIAL_LINKS.linkedin || "https://www.linkedin.com/company/neuravolt-technology/posts/?feedView=all"}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      title="Connect on LinkedIn"
                      className="w-8 h-8 rounded-lg bg-black/5 hover:bg-[#0A66C2] hover:text-white flex items-center justify-center text-neutral-600 transition-colors"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                    <a
                      href={SOCIAL_LINKS.instagram || "https://instagram.com/we.neuravolt"}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      title="Follow on Instagram"
                      className="w-8 h-8 rounded-lg bg-black/5 hover:bg-pink-600 hover:text-white flex items-center justify-center text-neutral-600 transition-colors"
                    >
                      <Instagram className="w-4 h-4" />
                    </a>
                    <a
                      href={SOCIAL_LINKS.github || "https://github.com/neuravolt-technologies"}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="GitHub"
                      title="View GitHub Repositories"
                      className="w-8 h-8 rounded-lg bg-black/5 hover:bg-black hover:text-white flex items-center justify-center text-neutral-600 transition-colors"
                    >
                      <Github className="w-4 h-4" />
                    </a>
                    <a
                      href={SOCIAL_LINKS.google_maps || "https://www.google.com/maps/place/Neuravolt+Technology/data=!4m2!3m1!1s0x0:0x47b13dd0dbd4c8f5"}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Google Maps Location & Reviews"
                      title="View on Google Maps"
                      className="w-8 h-8 rounded-lg bg-black/5 hover:bg-[#FF5A1F] hover:text-white flex items-center justify-center text-neutral-600 transition-colors"
                    >
                      <MapPin className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.4}>
                <div className="pt-3 border-t border-black/5">
                  <span className="font-bold text-xs uppercase tracking-wider text-[#FF5A1F] block">Corporate Office (HQ)</span>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed mt-0.5">{settings.corporate_address || CORPORATE_ADDRESS}</p>
                </div>
              </Reveal>

              <Reveal delay={0.45}>
                <div className="pt-2 border-t border-black/5">
                  <span className="font-bold text-xs uppercase tracking-wider text-[#FF5A1F] block">Corporate Office (Gurugram Branch)</span>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed mt-0.5">{settings.corporate_address_2 || CORPORATE_ADDRESS_2}</p>
                </div>
              </Reveal>

              <Reveal delay={0.5}>
                <div className="pt-2 border-t border-black/5">
                  <span className="font-bold text-xs uppercase tracking-wider text-neutral-500 block">Registered Office</span>
                  <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed mt-0.5">{settings.registered_address || REGISTERED_ADDRESS}</p>
                </div>
              </Reveal>
            </div>


            <Reveal delay={0.6}>
              <div className="mt-8 rounded-2xl overflow-hidden card-soft">
                <iframe title="map" src="https://www.google.com/maps?q=Gwalior,Madhya+Pradesh,India&output=embed" className="w-full h-48 border-0" loading="lazy" />
              </div>
            </Reveal>
          </div>



          <Reveal delay={0.2}>
            <form onSubmit={submit} data-testid="contact-form" className="card-soft rounded-3xl p-7 sm:p-9 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <input data-testid="contact-company" className={inputCls} placeholder="Company" value={form.company} onChange={(e) => set("company", e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} />
                <input data-testid="contact-phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Select value={form.service} onValueChange={(v) => set("service", v)}>
                  <SelectTrigger data-testid="contact-service" className={inputCls + " h-auto"}><SelectValue placeholder="Service" /></SelectTrigger>
                  <SelectContent>{SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                  <SelectTrigger data-testid="contact-budget" className={inputCls + " h-auto"}><SelectValue placeholder="Budget" /></SelectTrigger>
                  <SelectContent>{BUDGET_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <textarea data-testid="contact-message" rows={4} className={inputCls + " resize-none"} placeholder="Tell us about your project *" value={form.message} onChange={(e) => set("message", e.target.value)} />
              <Magnetic className="w-full">
                <button data-testid="contact-submit" disabled={loading} className="group w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors disabled:opacity-60">
                  {loading ? "Sending..." : "Submit Inquiry"}{!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
                </button>
              </Magnetic>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
